import { c as _c } from "react/compiler-runtime";
import { Button } from '@/components/Atoms/Button/Button';
import popoverStyles from '@/components/Atoms/Tooltip/PopoverContent.module.css';
import { CsrfForm } from '@/components/Molecules/CsrfForm/CsrfForm';
import { useHintPopover } from '@/hooks/useHintPopover';
import useTranslation from '@/hooks/useTranslation';
import type { IAnalyticsTraceableElement } from '@/interfaces';
import { trackEvent } from '@/services/Analytics/BBAnalytics';
import { TrackingAction, TrackingCategory } from '@/services/Analytics/bnefAnalyticsSettings';
import { toast } from '@/services/Toast/toastStore';
import savedStatesScheduler from '@/services/UserActivity/SavedStatesScheduler';
import classNames from '@/utils/classNames';
import StarIcon from '@bloomberg/web-icons-react/Star';
import StarOutlineIcon from '@bloomberg/web-icons-react/StarOutline';
import type { ButtonHTMLAttributes, FC } from 'react';
import { useEffect, useId, useState } from 'react';
import styles from './SaveButton.module.css';

/**
 * api/common/src/Domain/Enums/SavedItemContentType.php
 */
const SUPPORTED_TYPES = new Set(['tool', 'eventsession', 'insight', 'interactivedataset', 'news', 'short', 'analystreaction', 'theme', 'podcast', 'policy', 'video', 'country', 'organization']);

/**
 * A save-state aware button component to be used in content views.
 */
export const SaveButton: FC<ISaveButtonProps> = t0 => {
  const $ = _c(62);
  let analyticsBaseId;
  let ariaDescribedBy;
  let id;
  let itemName;
  let rest;
  let t1;
  let type;
  if ($[0] !== t0) {
    ({
      type,
      id,
      itemName,
      analyticsBaseId,
      withText: t1,
      "aria-describedby": ariaDescribedBy,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = analyticsBaseId;
    $[2] = ariaDescribedBy;
    $[3] = id;
    $[4] = itemName;
    $[5] = rest;
    $[6] = t1;
    $[7] = type;
  } else {
    analyticsBaseId = $[1];
    ariaDescribedBy = $[2];
    id = $[3];
    itemName = $[4];
    rest = $[5];
    t1 = $[6];
    type = $[7];
  }
  const withText = t1 === undefined ? true : t1;
  const {
    t
  } = useTranslation();
  const [savedStatus, setSaved] = useState();
  const [updating, setUpdating] = useState(false);
  const {
    popoverRef,
    openPopover,
    closePopover
  } = useHintPopover();
  const tooltipId = useId();
  let t2;
  if ($[8] !== id) {
    t2 = id.toLowerCase();
    $[8] = id;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const itemKey = `${type}-${t2}`;
  let t3;
  if ($[10] !== type) {
    t3 = SUPPORTED_TYPES.has(type);
    $[10] = type;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const isSupported = t3;
  let t4;
  let t5;
  if ($[12] !== isSupported || $[13] !== itemKey) {
    t4 = () => {
      if (!isSupported) {
        return;
      }
      return savedStatesScheduler.getInstance().registerItem(itemKey, x => setSaved(x));
    };
    t5 = [itemKey, isSupported];
    $[12] = isSupported;
    $[13] = itemKey;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  if (!isSupported) {
    return null;
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setUpdating(true);
    };
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const onSubmitStart = t6;
  let t7;
  if ($[17] !== analyticsBaseId || $[18] !== itemKey) {
    t7 = response => {
      const isSaved = response.saved;
      setSaved(isSaved);
      const event = new CustomEvent(savedStatesScheduler.CHANGE_EVENT, {
        detail: {
          key: itemKey,
          value: isSaved
        }
      });
      document.dispatchEvent(event);
      trackEvent(isSaved ? TrackingAction.SaveItem : TrackingAction.UnsaveItem, TrackingCategory.SaveButton, analyticsBaseId);
      setUpdating(false);
    };
    $[17] = analyticsBaseId;
    $[18] = itemKey;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const onSubmitEnd = t7;
  let t8;
  if ($[20] !== t) {
    t8 = () => {
      setUpdating(false);
      toast.error(t("common:Forms.EditForm.save_error"));
    };
    $[20] = t;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const onSubmitError = t8;
  let t9;
  if ($[22] !== savedStatus) {
    t9 = savedStatus ? <StarIcon /> : <StarOutlineIcon />;
    $[22] = savedStatus;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const icon = t9;
  const t10 = savedStatus ? "common:SaveButton.saved" : "common:SaveButton.save";
  let t11;
  if ($[24] !== t || $[25] !== t10) {
    t11 = t(t10);
    $[24] = t;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const text = t11;
  let t12;
  if ($[27] !== itemName || $[28] !== savedStatus || $[29] !== t) {
    t12 = itemName ? t(savedStatus ? "common:SaveButton.remove_named" : "common:SaveButton.add_named", {
      itemName
    }) : t(savedStatus ? "common:SaveButton.remove" : "common:SaveButton.add");
    $[27] = itemName;
    $[28] = savedStatus;
    $[29] = t;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const tooltipText = t12;
  let t13;
  if ($[31] !== ariaDescribedBy || $[32] !== tooltipId) {
    t13 = [ariaDescribedBy, tooltipId].filter(Boolean);
    $[31] = ariaDescribedBy;
    $[32] = tooltipId;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  const describedBy = t13.join(" ");
  let t14;
  if ($[34] !== id || $[35] !== type) {
    t14 = {
      id,
      type
    };
    $[34] = id;
    $[35] = type;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = savedStatus === undefined;
  let t16;
  if ($[37] !== t15) {
    t16 = classNames(styles.button, popoverStyles.anchorTrigger, {
      [styles.loading]: t15
    });
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  const t17 = withText ? text : null;
  let t18;
  if ($[39] !== closePopover || $[40] !== describedBy || $[41] !== icon || $[42] !== openPopover || $[43] !== rest || $[44] !== savedStatus || $[45] !== t16 || $[46] !== t17 || $[47] !== text || $[48] !== updating) {
    t18 = <Button kind="tertiary" icon={icon} aria-label={text} isLoading={updating} type="submit" disabled={updating} aria-pressed={savedStatus} className={t16} {...rest} aria-describedby={describedBy} onMouseEnter={openPopover} onMouseLeave={closePopover} onFocus={openPopover} onBlur={closePopover}>{t17}</Button>;
    $[39] = closePopover;
    $[40] = describedBy;
    $[41] = icon;
    $[42] = openPopover;
    $[43] = rest;
    $[44] = savedStatus;
    $[45] = t16;
    $[46] = t17;
    $[47] = text;
    $[48] = updating;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== tooltipText) {
    t19 = <span className={popoverStyles.popoverInner}>{tooltipText}</span>;
    $[50] = tooltipText;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== popoverRef || $[53] !== t19 || $[54] !== tooltipId) {
    t20 = <span id={tooltipId} ref={popoverRef} role="tooltip" popover="hint" className={popoverStyles.popover}>{t19}</span>;
    $[52] = popoverRef;
    $[53] = t19;
    $[54] = tooltipId;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== onSubmitEnd || $[57] !== onSubmitError || $[58] !== t14 || $[59] !== t18 || $[60] !== t20) {
    t21 = <CsrfForm className={popoverStyles.anchorScope} apiUrl="/activity/persist_event" values={t14} onSubmitStart={onSubmitStart} onSubmitEnd={onSubmitEnd} onSubmitError={onSubmitError}>{t18}{t20}</CsrfForm>;
    $[56] = onSubmitEnd;
    $[57] = onSubmitError;
    $[58] = t14;
    $[59] = t18;
    $[60] = t20;
    $[61] = t21;
  } else {
    t21 = $[61];
  }
  return t21;
};
interface ISaveButtonProps extends IAnalyticsTraceableElement, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  id: string;
  type: string;
  itemName?: string;
  withText?: boolean;
}